import React from 'react';
import { Button, Flex, FlexItem, Icon, Label, Tooltip } from '@patternfly/react-core';
import { OutlinedClockIcon, StopCircleIcon } from '@patternfly/react-icons';
import { useTimeTracker } from '../../context/TimeTrackerContext';
import { useTimer } from '../../context/TimerContext';
import { formatElapsedTime } from '../../utils';
import './TimerPill.css';

const MAX_SUMMARY_LENGTH = 40;
const MIN_CHARS_BEFORE_TRUNCATE = 4;

const truncateSummary = (summary: string): string => {
  if (summary.length <= MAX_SUMMARY_LENGTH) {
    return summary;
  }
  // Per UX.md: preserve at least 4 characters, use ellipsis
  const truncated = summary.substring(0, MAX_SUMMARY_LENGTH - 1).trim();
  if (truncated.length < MIN_CHARS_BEFORE_TRUNCATE) {
    return summary.substring(0, MIN_CHARS_BEFORE_TRUNCATE) + '…';
  }
  return truncated + '…';
};

export const TimerPill: React.FC = () => {
  const { stopTimer } = useTimeTracker();
  const { activeTimer, elapsedTime } = useTimer();

  if (!activeTimer) {
    return null;
  }

  const handleStop = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await stopTimer().catch(() => {});
  };

  const truncatedSummary = truncateSummary(activeTimer.issueSummary);

  // Per UX.md: Tooltip should add value, not repeat visible text
  const tooltipContent = (
    <div className="timer-bar-tooltip">
      <strong>Tracking time</strong>
      <br />
      {activeTimer.issueKey}: {activeTimer.issueSummary}
    </div>
  );

  return (
    <Tooltip content={tooltipContent} position="bottom">
      <div className="timer-bar" role="status" aria-label={`Tracking ${activeTimer.issueKey}`}>
        <Flex alignItems={{ default: 'alignItemsCenter' }} gap={{ default: 'gapSm' }}>
          <FlexItem>
            <Icon status="success" className="timer-bar-icon">
              <OutlinedClockIcon />
            </Icon>
          </FlexItem>
          <FlexItem>
            <Label color="blue" isCompact>
              {activeTimer.issueKey}
            </Label>
          </FlexItem>
          <FlexItem className="timer-bar-summary">
            {truncatedSummary}
          </FlexItem>
          <FlexItem>
            <span className="timer-bar-time" aria-live="polite">
              {formatElapsedTime(elapsedTime)}
            </span>
          </FlexItem>
          <FlexItem>
            {/* Per UX.md: Use secondary style for stop actions, not danger */}
            <Button
              variant="secondary"
              className="timer-bar-stop"
              icon={<StopCircleIcon />}
              onClick={handleStop}
              aria-label="Stop"
              size="sm"
            >
              Stop
            </Button>
          </FlexItem>
        </Flex>
      </div>
    </Tooltip>
  );
};
